import {
  AfterViewInit,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { AsyncPipe, DOCUMENT, NgClass, NgIf } from '@angular/common';
import {
  debounceTime,
  distinctUntilChanged,
  Observable,
  Subject,
  Subscription,
  take,
  takeUntil,
} from 'rxjs';
import { Store } from '@ngrx/store';
import {
  selectCredits,
  selectAITokens,
  selectLoading,
  selectError,
  selectUser,
} from '../../state/subscription/subscription.selectors';
import * as SubscriptionActions from '../../state/subscription/subscription.actions';
import { AuthService } from '../../services/auth.service';
import { SubscriptionService } from '../../services/subscription.service';
import { EventTypes } from '../../event-types';
import { SharedService } from '../../services/shared.service';
import { TranslateModule } from '@ngx-translate/core';
import { updateUser } from '../../state/auth/auth.actions';
import { updateSubscription } from '../../state/subscription/subscription.actions';
import { setIsLoggedIn } from '../../state/auth/auth.actions';
import { Router } from '@angular/router';

@Component({
  selector: 'app-subscription-header',
  standalone: true,
  imports: [AsyncPipe, NgIf, TranslateModule, NgClass],
  templateUrl: './subscription-header.component.html',
  styleUrl: './subscription-header.component.scss',
})
export class SubscriptionHeaderComponent implements OnInit, OnDestroy {
  private unsubscribeAll = new Subject<void>();

  isLoggedIn: boolean = false;

  credits$: Observable<number | null>;
  currentCredits: number = 0;
  isFading: boolean = false;

  aiTokens$: Observable<number | null>;
  user$: Observable<any | null>;
  loading$: Observable<boolean>;
  error$: Observable<any>;

  user: any;
  subscription: any;

  private isLoggedInSubscription: Subscription;

  constructor(
    private store: Store,
    private authService: AuthService,
    private subscriptionService: SubscriptionService,
    private sharedService: SharedService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.authService.authUser().subscribe((user) => {
      this.user = user;
    });

    this.subscriptionService.subscriptionData().subscribe((subscription) => {
      this.subscription = subscription;
    });

    this.isLoggedInSubscription = this.authService
      .isLoggedIn()
      .subscribe((isLoggedIn) => {
        this.isLoggedIn = isLoggedIn;

        if (isLoggedIn) {
          this.loadUserData();
        } else {
          // Unsubscribe when user is logged out
          this.unsubscribeUserData();
        }
      });

    this.sharedService.eventEmitter.subscribe((event) => {
      console.log('Event Received:', event.data);
      if (event.type === EventTypes.UPDATE_CREDITS) {
        this.loadUserData();
      }
    });

    this.credits$.subscribe((newCredits) => {
      if (newCredits < this.currentCredits) {
        this.triggerFadeAnimation();
      }
      this.currentCredits = newCredits;
    });
  }

  triggerFadeAnimation() {
    this.isFading = true;
    setTimeout(() => (this.isFading = false), 500); // Match animation duration
  }

  ngOnDestroy(): void {
    this.unsubscribeAll.next();
    this.unsubscribeAll.complete();
    this.isLoggedInSubscription.unsubscribe();
  }

  private loadUserData(): void {
    if (!this.isLoggedIn) {
      return; // If not logged in, do not proceed to load user data
    }

    this.credits$ = this.store.select(selectCredits);
    this.aiTokens$ = this.store.select(selectAITokens);
    this.user$ = this.store.select(selectUser);
    this.loading$ = this.store.select(selectLoading);
    this.error$ = this.store.select(selectError);

    this.loadCredits(); // This will now only be called if the user is logged in
    this.handleResponse();
  }

  private unsubscribeUserData(): void {
    console.log('unsubscribeUserData');

    this.isLoggedIn = false;
    this.store.dispatch(SubscriptionActions.clearSubscriptionData());
  }

  loadCredits(): void {
    if (!this.isLoggedIn) {
      console.log('Attempted to load credits when not logged in.');
      return;
    }
    this.store.dispatch(SubscriptionActions.loadCredits());

    this.store.select(selectError).subscribe((error) => {
      if (error && error.status === 401) {
        this.handleUnauthorized(); // Handle 401 Unauthorized
      }
    });
  }

  // Method to handle 401 Unauthorized error
  private handleUnauthorized(): void {
    localStorage.clear(); // Clear local storage
    sessionStorage.clear(); // Clear session storage

    // const updatedUser = {...this.user, has_completed_setup: 1};
    //this.store.dispatch(updateUser({ user: updatedUser }));

    this.store.dispatch(setIsLoggedIn(false));

    this.router.navigate(['/login']);

    // window.location = '/login'
  }

  private handleResponse(): void {
    this.error$.pipe(takeUntil(this.unsubscribeAll)).subscribe((error) => {
      if (error) {
        // console.error('Failed to load credits:', error);
      }
    });

    this.credits$.pipe(takeUntil(this.unsubscribeAll)).subscribe((credits) => {
      if (credits !== null) {
        // console.log('Credits loaded:', credits);
      }
    });

    this.aiTokens$
      .pipe(takeUntil(this.unsubscribeAll))
      .subscribe((aiTokens) => {
        if (aiTokens !== null) {
          // console.log('AI Tokens loaded:', aiTokens);
        }
      });

    this.user$.pipe(takeUntil(this.unsubscribeAll)).subscribe((storeUser) => {
      if (storeUser !== null) {
        // console.log('User loaded:', storeUser);

        if (this.isUserModified(storeUser)) {
          // console.log('User is modified.')
          const updatedUser = {
            ...this.user,
            name: storeUser.name,
            subscribed: storeUser.subscribed,
            on_trial: storeUser.on_trial,
            plan: storeUser.plan,
          };

          this.store.dispatch(updateUser({ user: updatedUser }));

          const updatedSubscription = {
            ...this.subscription,
            subscribed: storeUser.subscribed,
            on_trial: storeUser.on_trial,
          };

          this.store.dispatch(
            SubscriptionActions.storeSubscriptionData(updatedSubscription)
          );
        }
      }
    });
  }

  private isUserModified(storeUser: any): boolean {
    return (
      this.user.subscribed !== storeUser.subscribed ||
      this.user.on_trial !== storeUser.on_trial ||
      JSON.stringify(this.user.plan) !== JSON.stringify(storeUser.plan)
    );
  }
}
